import {
  buildApartmentFilterSearchParams,
  mergeUrlForView
} from './apartmentFilterUrl'
import {
  hasPersistedApartmentState,
  loadPersistedApartmentView
} from './apartmentFilterPersist'

/** List view: `/apartments` but not map or detail. */
export function isApartmentListPath(pathname: string): boolean {
  return /\/apartments$/.test(pathname)
}

export function isApartmentMapPath(pathname: string): boolean {
  return pathname.includes('/apartments-map')
}

export function isApartmentViewPath(pathname: string): boolean {
  return isApartmentListPath(pathname) || isApartmentMapPath(pathname)
}

/** Sidebar / nav `key` → list or map target, if it is an apartment route. */
export function matchApartmentNavTarget(navKey: string): 'list' | 'map' | null {
  if (navKey.includes('/apartments-map')) return 'map'
  if (/\/apartments$/.test(navKey)) return 'list'
  return null
}

/**
 * Build query string for list/map navigation from current URL or sessionStorage.
 */
export function getApartmentNavSearchParams(
  target: 'list' | 'map',
  currentSearchParams: URLSearchParams,
  pathname: string
): URLSearchParams {
  if (isApartmentViewPath(pathname)) {
    return mergeUrlForView(target, currentSearchParams)
  }

  const saved = loadPersistedApartmentView()
  if (hasPersistedApartmentState(saved)) {
    return buildApartmentFilterSearchParams(saved.filters, {
      listingType: saved.listingType,
      includeListParams: target === 'list',
      q: saved.q,
      page: saved.page,
      limit: saved.limit
    })
  }

  return buildApartmentFilterSearchParams(
    {},
    {
      listingType: 'rent',
      includeListParams: target === 'list'
    }
  )
}

export function buildApartmentNavHref(
  routePath: string,
  target: 'list' | 'map',
  currentSearchParams: URLSearchParams,
  pathname: string
): string {
  const params = getApartmentNavSearchParams(target, currentSearchParams, pathname)
  const query = params.toString()
  return query ? `${routePath}?${query}` : routePath
}

export function resolveApartmentNavHref(
  navKey: string,
  currentSearchParams: URLSearchParams,
  pathname: string
): string | null {
  const target = matchApartmentNavTarget(navKey)
  if (!target) return null
  return buildApartmentNavHref(navKey, target, currentSearchParams, pathname)
}
